import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {handleHighlightedElementChange, handleOnBeforeAfterChange} from '../../../components/slides/form/slide.form.utils';
import {IElement} from '../../../incase-domain/elements';
import {IElementBase} from '../../../incase-domain/elements/base/elementBase';
import {highlightedElementIndexSelector, selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import {useSlideUpdateContext} from '../../../ui/context/SetSlideUpdateContext';

export type UpdateSlideElementHook = {
    single: <T extends IElementBase>(field: keyof T | string, value: unknown) => void;
    multiple: <T extends IElementBase>(fields: (keyof T)[] | string[], values: unknown[]) => void;
};

export function useSetSlideElement(element: IElement, highlightedElementIndex: number): UpdateSlideElementHook {
    const slide = useSelector(selectedSlideSelector);
    const {setSlide} = useSlideUpdateContext();

    function elementChangeHandler<T extends IElementBase>(field: keyof T | string, value: unknown): void {
        const newElement = handleOnBeforeAfterChange({...element, [field]: value}, field as keyof IElement, value);
        handleHighlightedElementChange(slide, highlightedElementIndex, setSlide, newElement);
    }
    type elementChangeHandlerParams = Parameters<typeof elementChangeHandler>;

    function multipleElementChangeHandler<T extends IElementBase>(fields: (keyof T)[], values: unknown[]): void {
        let newElement = {...element};
        fields.forEach((field, fieldIndex) => {
            newElement = {...newElement, [field]: values[fieldIndex]};
        });
        handleHighlightedElementChange(slide, highlightedElementIndex, setSlide, newElement);
    }
    type multipleElementChangeHandlerParams = Parameters<typeof multipleElementChangeHandler>;

    return {
        single: <T extends IElementBase>(field: keyof T | string, value: unknown) =>
            elementChangeHandler(field as elementChangeHandlerParams[0], value),
        multiple: <T extends IElementBase>(fields: (keyof T)[] | string[], values: unknown[]) =>
            multipleElementChangeHandler(fields as multipleElementChangeHandlerParams[0], values),
    };
}

export function useHighlightedElement(): IElement {
    const slide = useSelector(selectedSlideSelector);
    const highlightedElementIndex = useSelector(highlightedElementIndexSelector);

    return useMemo(() => {
        return slide.elements[highlightedElementIndex] || {};
    }, [slide.elements, highlightedElementIndex]);
}

export function useSetHighlightedElement(): UpdateSlideElementHook {
    const highlightedElementIndex = useSelector(highlightedElementIndexSelector);
    const highlightedElement = useHighlightedElement();

    return useSetSlideElement(highlightedElement, highlightedElementIndex);
}
